var _a;
import { Decimal as DecimalJs } from 'decimal.js';
import { z } from 'zod';
/**
 * Dinero de Paguz. Reglas (arquitectura §6):
 * - API: `{ "amount": "85000.00", "currency": "COP" }` (string decimal, 2 decimales).
 * - BD: `numeric(20,2)`.
 * - Código: este helper (decimal.js). **Nunca** `number` de punto flotante.
 * - Cuotas redondeadas a peso entero (`roundToPeso`); la última absorbe la diferencia (credit-math).
 *
 * `Money` es inmutable. No acepta `number` a propósito: construye desde string, bigint o Decimal.
 */
/** Constructor de Decimal aislado (no modifica la configuración global de decimal.js). */
export const Decimal = DecimalJs.clone({
    precision: 40,
    rounding: DecimalJs.ROUND_HALF_UP,
    toExpNeg: -40,
    toExpPos: 40,
});
export const CURRENCIES = ['COP'];
export const CurrencySchema = z.enum(CURRENCIES);
/** String decimal con exactamente 2 decimales (`"85000.00"`, `"-120.50"`). */
export const DECIMAL_2_PATTERN = /^-?\d{1,18}\.\d{2}$/;
export const DecimalAmountSchema = z
    .string()
    .regex(DECIMAL_2_PATTERN, 'Debe ser un decimal con 2 decimales, p. ej. "85000.00"');
/** Forma serializada de `Money` (API, eventos, JSON en BD). */
export const MoneySchema = z
    .object({
    amount: DecimalAmountSchema,
    currency: CurrencySchema,
})
    .meta({
    id: 'Money',
    description: 'Monto en string decimal con 2 decimales. Nunca número flotante.',
});
/** Monto estrictamente positivo (compras, pagos). */
export const PositiveMoneySchema = MoneySchema.refine((m) => new Decimal(m.amount).gt(0), {
    message: 'El monto debe ser mayor que cero',
});
const ROUNDING = {
    HALF_UP: DecimalJs.ROUND_HALF_UP,
    DOWN: DecimalJs.ROUND_DOWN,
    UP: DecimalJs.ROUND_UP,
    HALF_EVEN: DecimalJs.ROUND_HALF_EVEN,
};
function toDecimal(value) {
    if (value instanceof Money)
        return value.toDecimal();
    if (typeof value === 'bigint')
        return new Decimal(value.toString());
    if (typeof value === 'string') {
        if (!/^-?\d+(\.\d+)?$/.test(value.trim())) {
            throw new TypeError(`Monto inválido: "${value}"`);
        }
        return new Decimal(value.trim());
    }
    if (Decimal.isDecimal(value))
        return new Decimal(value);
    throw new TypeError('Money solo acepta string, bigint o Decimal (nunca number)');
}
export class Money {
    currency;
    #value;
    constructor(value, currency) {
        // Normaliza a 2 decimales (centavos) con HALF_UP.
        this.#value = value.toDecimalPlaces(2, DecimalJs.ROUND_HALF_UP);
        this.currency = currency;
    }
    static of(value, currency = 'COP') {
        if (value instanceof _a) {
            assertCurrency(value.currency, currency);
            return value;
        }
        return new _a(toDecimal(value), currency);
    }
    static cop(value) {
        return _a.of(value, 'COP');
    }
    static zero(currency = 'COP') {
        return new _a(new Decimal(0), currency);
    }
    static fromJSON(json) {
        const parsed = MoneySchema.parse(json);
        return new _a(new Decimal(parsed.amount), parsed.currency);
    }
    /** Suma una lista (vacía → cero). */
    static sum(values, currency = 'COP') {
        return values.reduce((acc, v) => acc.plus(v), _a.zero(currency));
    }
    static max(a, b) {
        return a.gte(b) ? a : b;
    }
    static min(a, b) {
        return a.lte(b) ? a : b;
    }
    plus(other) {
        const o = this.#coerce(other);
        return new _a(this.#value.plus(o.#value), this.currency);
    }
    minus(other) {
        const o = this.#coerce(other);
        return new _a(this.#value.minus(o.#value), this.currency);
    }
    /** Multiplica por un factor decimal (string/Decimal), p. ej. una tasa. Resultado a 2 decimales HALF_UP. */
    times(factor) {
        return new _a(this.#value.times(new Decimal(factor)), this.currency);
    }
    /** Redondea a peso entero (sin centavos). Por defecto HALF_UP (`PESO_HALF_UP`). */
    roundToPeso(mode = 'HALF_UP') {
        return new _a(this.#value.toDecimalPlaces(0, ROUNDING[mode]), this.currency);
    }
    negated() {
        return new _a(this.#value.negated(), this.currency);
    }
    abs() {
        return new _a(this.#value.abs(), this.currency);
    }
    isZero() {
        return this.#value.isZero();
    }
    isPositive() {
        return this.#value.gt(0);
    }
    isNegative() {
        return this.#value.lt(0);
    }
    eq(other) {
        return this.#value.eq(this.#coerce(other).#value);
    }
    gt(other) {
        return this.#value.gt(this.#coerce(other).#value);
    }
    gte(other) {
        return this.#value.gte(this.#coerce(other).#value);
    }
    lt(other) {
        return this.#value.lt(this.#coerce(other).#value);
    }
    lte(other) {
        return this.#value.lte(this.#coerce(other).#value);
    }
    compare(other) {
        const c = this.#value.comparedTo(this.#coerce(other).#value);
        return c < 0 ? -1 : c > 0 ? 1 : 0;
    }
    /** Copia del valor como Decimal (para credit-math). */
    toDecimal() {
        return new Decimal(this.#value);
    }
    /** String con 2 decimales: `"85000.00"`. */
    toString() {
        return this.#value.toFixed(2);
    }
    toJSON() {
        return { amount: this.toString(), currency: this.currency };
    }
    /** Formato COP para UI y mensajes: `$ 1.200.000` (sin centavos si son cero). */
    format(options = {}) {
        const negative = this.#value.isNegative();
        const abs = this.#value.abs();
        const showCents = options.cents ?? !abs.isInteger();
        const fixed = abs.toFixed(showCents ? 2 : 0);
        const [intPart = '0', decPart] = fixed.split('.');
        const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return `${negative ? '-' : ''}$ ${grouped}${decPart ? `,${decPart}` : ''}`;
    }
    #coerce(other) {
        if (other instanceof _a) {
            assertCurrency(this.currency, other.currency);
            return other;
        }
        return new _a(toDecimal(other), this.currency);
    }
}
_a = Money;
function assertCurrency(a, b) {
    const [x, y] = [a, b];
    if (x !== y)
        throw new TypeError(`Monedas distintas: ${String(x)} vs ${String(y)}`);
}
/** Zod: acepta `MoneyJson` y lo transforma a `Money` (útil en dominio). */
export const MoneyValueSchema = MoneySchema.transform((m) => Money.fromJSON(m));
export function money(amount, currency = 'COP') {
    return Money.of(amount, currency).toJSON();
}
//# sourceMappingURL=money.js.map